import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import toast from 'react-hot-toast';
import { FiUpload, FiX, FiFile, FiDownload, FiTrash2, FiPackage, FiCheckCircle, FiEye } from 'react-icons/fi';
import {
  getWeaponDocuments,
  addWeaponDocument,
  deleteWeaponDocument,
  WeaponDocument,
} from '../service/weaponDocumentService';
import PDFViewerModal from '../../../deployment/components/PDFViewerModal';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const WeaponSection = styled.div`
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
`;

const WeaponHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

const WeaponIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[600]};
`;

const WeaponInfo = styled.div`
  flex: 1;
`;

const WeaponName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const WeaponDetails = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const DocumentStatus = styled.div<{ hasDocuments: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${({ hasDocuments }) =>
    hasDocuments ? theme.colors.accent.success : theme.colors.accent.warning};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const UploadSection = styled.div`
  margin-bottom: ${theme.spacing[3]};
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  height: 40px;
  white-space: nowrap;

  &:hover {
    background-color: ${theme.colors.primary[600]};
  }

  input {
    display: none;
  }
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]};
  background-color: ${theme.colors.light.background.secondary};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex: 1;
`;

const DocumentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${theme.colors.primary[50]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const DocumentMeta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.delete:hover {
    color: ${theme.colors.accent.danger};
    background-color: ${theme.colors.accent.danger}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[4]};
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const FileInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[2]};
  align-items: flex-start;
`;

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 0;
`;

interface WeaponDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  weaponId: string;
  weaponName: string;
  companyId: string;
  onDocumentsUpdated?: () => void;
}

const WeaponDocumentsModal: React.FC<WeaponDocumentsModalProps> = ({
  isOpen,
  onClose,
  weaponId,
  weaponName,
  companyId,
  onDocumentsUpdated,
}) => {
  const [documents, setDocuments] = useState<WeaponDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<{ [documentId: string]: boolean }>({});
  const [documentName, setDocumentName] = useState('');
  const [viewingPDF, setViewingPDF] = useState<{ url: string; name: string; size?: number; uploadedAt?: Date | string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
    }
  }, [isOpen, weaponId, companyId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await getWeaponDocuments(weaponId, companyId);
      setDocuments(docs);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type - only allow PDF
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploading(true);
    try {
      await addWeaponDocument(weaponId, companyId, file, documentName);
      toast.success('Document uploaded successfully');
      setDocumentName('');
      await loadDocuments();
      if (onDocumentsUpdated) {
        onDocumentsUpdated();
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeleting((prev) => ({ ...prev, [documentId]: true }));
    try {
      await deleteWeaponDocument(weaponId, companyId, documentId);
      toast.success('Document deleted successfully');
      await loadDocuments();
      if (onDocumentsUpdated) {
        onDocumentsUpdated();
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(error.message || 'Failed to delete document');
    } finally {
      setDeleting((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (date: Date | string | any): string => {
    if (!date) return 'N/A';
    
    let d: Date;
    
    // Handle Firestore Timestamp
    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      d = date.toDate();
    } 
    // Handle string
    else if (typeof date === 'string') {
      d = new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
    }
    // Handle Date object
    else if (date instanceof Date) {
      d = date;
    }
    // Handle object with seconds/nanoseconds (Firestore Timestamp structure)
    else if (date && typeof date === 'object' && (date.seconds || date._seconds)) {
      const seconds = date.seconds || date._seconds || 0;
      const nanoseconds = date.nanoseconds || date._nanoseconds || 0;
      d = new Date(seconds * 1000 + nanoseconds / 1000000);
    }
    else {
      return 'Invalid Date';
    }
    
    if (isNaN(d.getTime())) {
      return 'Invalid Date';
    }
    
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasDocuments = documents && documents.length > 0;
  const fileInputId = `weapon-file-input-${weaponId}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Weapon Documents - ${weaponName}`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <ModalContent>
        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
            Loading documents...
          </div>
        ) : (
          <WeaponSection>
            <WeaponHeader>
              <WeaponIcon>
                <FiPackage size={24} />
              </WeaponIcon>
              <WeaponInfo>
                <WeaponName>{weaponName}</WeaponName>
                <WeaponDetails>Weapon ID: {weaponId}</WeaponDetails>
              </WeaponInfo>
              <DocumentStatus hasDocuments={!!hasDocuments}>
                {hasDocuments ? (
                  <>
                    <FiCheckCircle size={18} />
                    <span>{documents.length} document(s)</span>
                  </>
                ) : (
                  <>
                    <FiX size={18} />
                    <span>Pending documents</span>
                  </>
                )}
              </DocumentStatus>
            </WeaponHeader>

            <UploadSection>
              <FileInputContainer>
                <InputWrapper>
                  <Input
                    placeholder="Document name (e.g., License, Registration, etc.)"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    fullWidth
                    dark
                    helperText="Only PDF files are allowed"
                  />
                </InputWrapper>
                <ButtonWrapper>
                  <UploadButton htmlFor={fileInputId}>
                    <FiUpload size={18} />
                    {uploading ? 'Uploading...' : 'Upload PDF'}
                    <input
                      id={fileInputId}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileSelect(file);
                        }
                        // Reset input
                        e.target.value = '';
                      }}
                      disabled={uploading}
                    />
                  </UploadButton>
                </ButtonWrapper>
              </FileInputContainer>
            </UploadSection>

            {hasDocuments ? (
              <DocumentsList>
                {documents.map((document) => (
                  <DocumentItem key={document.id}>
                    <DocumentInfo>
                      <DocumentIcon>
                        <FiFile size={20} />
                      </DocumentIcon>
                      <DocumentDetails>
                        <DocumentName>{document.name}</DocumentName>
                        <DocumentMeta>
                          {formatFileSize(document.fileSize)} • Uploaded{' '}
                          {formatDate(document.uploadedAt)}
                        </DocumentMeta>
                      </DocumentDetails>
                    </DocumentInfo>
                    <DocumentActions>
                      <ActionButton
                        onClick={() => {
                          setViewingPDF({
                            url: document.url,
                            name: document.name,
                            size: document.fileSize,
                            uploadedAt: document.uploadedAt,
                          });
                        }}
                        title="View Document"
                      >
                        <FiEye size={18} />
                      </ActionButton>
                      <ActionButton
                        onClick={() => window.open(document.url, '_blank')}
                        title="Download"
                      >
                        <FiDownload size={18} />
                      </ActionButton>
                      <ActionButton
                        className="delete"
                        onClick={() => handleDeleteDocument(document.id)}
                        disabled={deleting[document.id]}
                        title="Delete"
                      >
                        <FiTrash2 size={18} />
                      </ActionButton>
                    </DocumentActions>
                  </DocumentItem>
                ))}
              </DocumentsList>
            ) : (
              <EmptyState>No documents uploaded yet</EmptyState>
            )}
          </WeaponSection>
        )}
      </ModalContent>

      {viewingPDF && (
        <PDFViewerModal
          isOpen={!!viewingPDF}
          onClose={() => setViewingPDF(null)}
          pdfUrl={viewingPDF.url}
          documentName={viewingPDF.name}
          documentSize={viewingPDF.size}
          uploadedAt={viewingPDF.uploadedAt}
        />
      )}
    </Modal>
  );
};

export default WeaponDocumentsModal;

